import * as React from 'react';
import { gql } from '@apollo/client';
import useBookingQuery from 'booking/hooks/useBookingQuery';
import { Slot, TimeSlotsQuery } from 'booking/lib/types';
import { parseSlots } from '../../lib/utils';

interface SlotsLoaderProps {
    locationId: string;
    day: string;
    setSlots: (
        locationId: string,
        isLoading: boolean,
        slots: ReadonlyArray<Slot>,
        error?: unknown,
    ) => void;
}

const query = gql`
    query nearbyTimeSlots($locationId: InternalId!, $day: DateString!) {
        timeSlots(
            locationId: $locationId
            from: $day
            to: $day
            firstAvailable: false
        ) {
            timezone
            slots
        }
    }
`;

interface NearbyTimeSlotsVariables {
    day: string;
    locationId: string;
}

export default function SlotsLoader({
    locationId,
    day,
    setSlots,
}: SlotsLoaderProps) {
    const { data, loading, error } = useBookingQuery<
        TimeSlotsQuery,
        NearbyTimeSlotsVariables
    >(query, {
        fetchPolicy: 'no-cache',
        displayName: `nearbyTimeSlots(${locationId})`,
        variables: { locationId, day },
    });

    React.useEffect(() => {
        const isLoading = loading || (!data?.timeSlots && !error);

        if (isLoading) {
            setSlots(locationId, isLoading, []);
        } else if (data?.timeSlots) {
            setSlots(
                locationId,
                isLoading,
                parseSlots(data.timeSlots.timezone, data.timeSlots.slots),
            );
        } else if (error) {
            setSlots(locationId, isLoading, [], error);
        }
    }, [data?.timeSlots, loading, setSlots, locationId, error]);

    return <span />;
}
